import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Lock, ShieldCheck, ExternalLink, RefreshCcw, Webhook, AlertTriangle,
  Check, Trash2, Eye, EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const PlatformStripePage = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [publicBaseUrl, setPublicBaseUrl] = useState('');
  const [autoWebhook, setAutoWebhook] = useState(true);
  const [showKey, setShowKey] = useState(false);

  const loadConfig = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/platform/stripe/config`, { withCredentials: true });
      setConfig(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao carregar configuração.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const saveKey = async () => {
    const trimmed = apiKey.trim();
    if (!trimmed.startsWith('sk_test_') && !trimmed.startsWith('sk_live_')) {
      toast.error('A chave precisa começar com sk_test_ ou sk_live_.');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/platform/stripe/config`,
        {
          api_key: trimmed,
          auto_setup_webhook: autoWebhook,
          public_base_url: publicBaseUrl.trim() || null,
        },
        { withCredentials: true }
      );
      setConfig(data);
      setApiKey('');
      setPublicBaseUrl('');
      toast.success(`Stripe configurado em modo ${data.mode.toUpperCase()}. ${data.webhook_endpoint_id ? 'Webhook criado.' : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao salvar a chave.');
    } finally {
      setSaving(false);
    }
  };

  const syncPlans = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/platform/plans/sync-stripe`, {}, { withCredentials: true });
      toast.success(`Sincronizado: ${data.synced.length} plano(s).`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao sincronizar.');
    } finally {
      setSyncing(false);
    }
  };

  const clearConfig = async () => {
    if (!window.confirm('Remover configuração do Stripe? Isso deleta o webhook endpoint e desabilita cobranças até reconfigurar.')) return;
    setClearing(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/platform/stripe/config`, { withCredentials: true });
      await loadConfig();
      toast.success('Configuração removida.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao remover.');
    } finally {
      setClearing(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto py-12 text-center text-slate-500">Carregando configuração...</div>
      </Layout>
    );
  }

  const isLive = config?.mode === 'live';

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="platform-stripe-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-outfit flex items-center gap-3">
              Configuração do Stripe
              {config?.enabled && (
                <Badge
                  variant="outline"
                  className={isLive
                    ? 'border-rose-500 text-rose-700 bg-rose-50 font-bold'
                    : 'border-amber-500 text-amber-700 bg-amber-50 font-bold'}
                  data-testid="stripe-mode-badge"
                >
                  {isLive ? 'MODO LIVE — COBRA DE VERDADE' : 'MODO TESTE'}
                </Badge>
              )}
            </h1>
            <p className="text-slate-600 font-manrope mt-2">
              Pagamentos recorrentes mensais para os planos Básico (R$ 499) e Pro (R$ 799).
            </p>
          </div>
          <Button variant="outline" onClick={loadConfig} className="gap-2" data-testid="stripe-refresh-btn">
            <RefreshCcw className="h-4 w-4" /> Atualizar
          </Button>
        </div>

        {/* Current status */}
        <Card className="border-slate-200">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-outfit font-bold text-lg text-slate-900">Status atual</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm font-manrope">
              <StatusRow
                ok={config?.api_key_set}
                label="Chave secreta"
                value={config?.api_key_masked || '—'}
              />
              <StatusRow
                ok={config?.webhook_secret_set}
                label="Webhook configurado"
                value={config?.webhook_endpoint_id || '—'}
              />
              <StatusRow
                ok={!!config?.account?.id}
                label="Conta Stripe"
                value={config?.account?.email || config?.account?.id || '—'}
              />
              <StatusRow
                ok={!!config?.webhook_url}
                label="URL do webhook"
                value={
                  config?.webhook_url ? (
                    <a href={config.webhook_url} target="_blank" rel="noreferrer noopener" className="text-emerald-700 hover:underline inline-flex items-center gap-1">
                      {config.webhook_url.replace(/^https?:\/\//, '')} <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : '—'
                }
              />
            </div>
            {config?.enabled && (
              <div className="flex gap-3 pt-2 flex-wrap">
                <Button onClick={syncPlans} disabled={syncing} variant="outline" className="gap-2" data-testid="stripe-sync-plans-btn">
                  <RefreshCcw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? 'Sincronizando...' : 'Sincronizar planos com Stripe'}
                </Button>
                <Button onClick={clearConfig} disabled={clearing} variant="outline" className="gap-2 text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="stripe-clear-btn">
                  <Trash2 className="h-4 w-4" />
                  {clearing ? 'Removendo...' : 'Remover configuração'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Setup form */}
        <Card className="border-slate-200">
          <CardContent className="p-6 space-y-5">
            <div>
              <h2 className="font-outfit font-bold text-lg text-slate-900 flex items-center gap-2">
                <Webhook className="h-5 w-5 text-emerald-600" />
                {config?.api_key_set ? 'Substituir chave' : 'Configurar agora'}
              </h2>
              <p className="text-sm text-slate-600 font-manrope mt-1">
                Cole sua <strong>Secret Key</strong> do Stripe. Encontre em{' '}
                <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer noopener" className="text-emerald-700 hover:underline inline-flex items-center gap-1">
                  Dashboard → Developers → API keys <ExternalLink className="h-3 w-3" />
                </a>
                .
              </p>
            </div>

            <div>
              <Label htmlFor="api_key">Secret key (sk_test_… ou sk_live_…)</Label>
              <div className="relative mt-1.5">
                <Input
                  id="api_key"
                  type={showKey ? 'text' : 'password'}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="sk_test_..."
                  autoComplete="off"
                  className="pr-10 font-mono text-sm"
                  data-testid="stripe-api-key-input"
                />
                <button
                  type="button"
                  onClick={() => setShowKey((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  aria-label="Mostrar/ocultar"
                >
                  {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {apiKey.startsWith('sk_live_') && (
                <div className="mt-2 flex items-start gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2.5 font-manrope">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>Você está prestes a salvar uma chave <strong>LIVE</strong>. Cobranças serão <strong>reais</strong>. Recomendado validar em <code>sk_test_</code> primeiro.</span>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="public_base_url">
                URL pública do app <span className="text-slate-400 font-normal">(opcional, mas obrigatório em produção)</span>
              </Label>
              <Input
                id="public_base_url"
                type="text"
                value={publicBaseUrl}
                onChange={(e) => setPublicBaseUrl(e.target.value)}
                placeholder="https://meadd.app"
                autoComplete="off"
                className="mt-1.5 font-mono text-sm"
                data-testid="stripe-public-url-input"
              />
              <p className="text-xs text-slate-500 font-manrope mt-1.5 leading-relaxed">
                Cole aqui o domínio público onde o app está rodando (ex: <code>https://meadd.app</code>).
                O Stripe vai enviar eventos para <code>{publicBaseUrl.trim() ? `${publicBaseUrl.trim().replace(/\/$/, '')}/api/webhook/stripe` : 'esta-url/api/webhook/stripe'}</code>.
                Se deixar em branco, usaremos a URL deste navegador — útil só em ambiente de teste.
              </p>
            </div>

            <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Webhook className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                <div className="text-sm font-manrope">
                  <div className="font-semibold text-slate-900">Criar webhook automaticamente</div>
                  <div className="text-slate-600 mt-0.5">
                    Vamos criar o endpoint <code className="text-xs bg-white px-1 py-0.5 rounded border">/api/webhook/stripe</code> no Stripe com os 6 eventos necessários e salvar o signing secret.
                  </div>
                </div>
              </div>
              <Switch checked={autoWebhook} onCheckedChange={setAutoWebhook} data-testid="stripe-auto-webhook-switch" />
            </div>

            <Button
              onClick={saveKey}
              disabled={saving || !apiKey.trim()}
              className="bg-slate-900 hover:bg-slate-800 text-white font-semibold w-full sm:w-auto"
              data-testid="stripe-save-config-btn"
            >
              <Lock className="h-4 w-4 mr-2" />
              {saving ? 'Validando e salvando...' : 'Salvar e configurar Stripe'}
            </Button>
          </CardContent>
        </Card>

        {/* Help block */}
        <Card className="border-emerald-200 bg-emerald-50/40">
          <CardContent className="p-6 space-y-3 text-sm font-manrope">
            <h3 className="font-outfit font-bold text-emerald-900 flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" />
              Passos antes de salvar
            </h3>
            <ol className="list-decimal list-inside space-y-1.5 text-emerald-900/90">
              <li>Em <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer noopener" className="underline">dashboard.stripe.com/apikeys</a>, ative o <strong>Test mode</strong> (toggle laranja no canto superior).</li>
              <li>Copie a <strong>Secret key</strong> que começa com <code>sk_test_</code>.</li>
              <li>Cole acima e deixe a opção "Criar webhook automaticamente" ligada.</li>
              <li>Clique em <strong>Salvar e configurar Stripe</strong>. O sistema vai criar o webhook e armazenar tudo.</li>
              <li>Depois clique em <strong>Sincronizar planos com Stripe</strong> para criar Produtos e Preços recorrentes.</li>
              <li>Teste no <code>/billing</code> usando cartão <code>4242 4242 4242 4242</code>.</li>
              <li>Quando estiver tudo OK, repita com a chave <code>sk_live_</code> para ir ao ar.</li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

const StatusRow = ({ ok, label, value }) => (
  <div className="flex items-start gap-3">
    <div className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 ${ok ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
      {ok ? <Check className="h-4 w-4" /> : <span className="h-2 w-2 bg-current rounded-full" />}
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className="font-mono text-xs text-slate-800 mt-0.5 break-all">{value}</div>
    </div>
  </div>
);

export default PlatformStripePage;
